import { useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, Calendar, Check, ChevronDown, Search, Star, Upload } from "lucide-react";
import { DemoCard, useGuide } from "./Guide";

const field = "w-full rounded-xl border border-white/15 bg-brand-ink px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-brand-green focus:outline-none";

export function MultiStepForm() {
    const { say } = useGuide();
    const [step, setStep] = useState(0);
    const steps = ["Who are you?", "What do you need?", "When?"];
    return (
        <DemoCard title="Multi-step form" hint="click next" testid="demo-multistep">
            <div className="mb-3 flex gap-1.5">
                {steps.map((_, i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-brand-green" : "bg-white/12"}`} />)}
            </div>
            <p className="text-sm font-bold">{steps[step]}</p>
            <input className={`${field} mt-2.5`} placeholder={["Your name", "20 shirts, front print", "Next Friday"][step]} data-testid="demo-multistep-input" />
            <button onClick={() => { const n = (step + 1) % 3; setStep(n); say("Long forms scare people off. Three short steps convert way better — we build them that way.", "multistep"); }}
                className="mt-3 w-full rounded-xl bg-brand-green px-4 py-2.5 text-sm font-black uppercase tracking-wide text-black" data-testid="demo-multistep-next">
                {step === 2 ? "Send it" : "Next"}
            </button>
        </DemoCard>
    );
}

export function LiveValidationForm() {
    const { say } = useGuide();
    const [v, setV] = useState("");
    const ok = /.+@.+\..+/.test(v);
    return (
        <DemoCard title="Live validation" hint="type an email" testid="demo-validation">
            <input value={v} onChange={(e) => { setV(e.target.value); if (e.target.value.length === 4) say("Validation as they type — no 'oops, invalid' surprise after they hit send.", "validation"); }}
                className={`${field} ${v && !ok ? "border-red-500/60" : ok ? "border-brand-green/60" : ""}`} placeholder="you@shop.com" data-testid="demo-validation-input" />
            <p className={`mt-2 flex items-center gap-1.5 text-xs ${!v ? "text-zinc-500" : ok ? "text-brand-green" : "text-red-400"}`}>
                {v && (ok ? <Check size={13} /> : <AlertCircle size={13} />)} {!v ? "Start typing…" : ok ? "Looks good — we'd send the quote here." : "That's not an email yet."}
            </p>
        </DemoCard>
    );
}

export function SliderQuote() {
    const { say } = useGuide();
    const [n, setN] = useState(24);
    const price = (n * (n >= 48 ? 12 : n >= 24 ? 14 : 17)).toFixed(0);
    return (
        <DemoCard title="Instant price slider" hint="drag it" testid="demo-slider">
            <input type="range" min="6" max="144" step="6" value={n} onChange={(e) => setN(+e.target.value)}
                onMouseUp={() => say("A live quote calculator. Customers self-serve the price and land in your inbox pre-sold.", "slider")}
                className="w-full accent-[#2563EB]" data-testid="demo-slider-input" />
            <div className="mt-3 flex items-end justify-between">
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">{n} shirts</span>
                <span className="font-display text-2xl font-black text-brand-green">${price}</span>
            </div>
        </DemoCard>
    );
}

export function StarRating() {
    const { say } = useGuide();
    const [r, setR] = useState(0);
    return (
        <DemoCard title="Star rating" hint="rate us" testid="demo-rating">
            <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} onClick={() => { setR(s); say(s >= 4 ? "Reviews collected right on your site, then shown off automatically. Social proof on autopilot." : "Low ratings can route straight to your phone instead of Google. Fix it before it's public.", "rating"); }}
                        data-testid={`demo-star-${s}`}>
                        <Star size={22} className={s <= r ? "fill-brand-green text-brand-green" : "text-zinc-600"} />
                    </button>
                ))}
            </div>
            <p className="mt-2 text-xs text-zinc-500">{r ? `${r} / 5 — thanks!` : "Tap a star"}</p>
        </DemoCard>
    );
}

export function FileDropDemo() {
    const { say } = useGuide();
    const [over, setOver] = useState(false);
    const [dropped, setDropped] = useState(false);
    return (
        <DemoCard title="Drag & drop upload" hint="drag a file over" testid="demo-drop">
            <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
                onDrop={(e) => { e.preventDefault(); setOver(false); setDropped(true); say("File uploads — artwork, PDFs, photos — straight into your system. This site already does it for print orders.", "drop"); }}
                onClick={() => { setDropped(true); say("Uploads with progress bars, previews and cloud storage. No more 'email me the file'.", "drop"); }}
                className={`flex h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-center transition-colors ${over ? "border-brand-green bg-brand-green/10" : "border-white/15"}`}
                data-testid="demo-drop-zone">
                <Upload size={20} className={dropped ? "text-brand-green" : "text-zinc-500"} />
                <p className="text-xs text-zinc-400">{dropped ? "logo-final-FINAL-v3.ai · uploaded" : "Drop artwork here"}</p>
            </div>
        </DemoCard>
    );
}

export function AutocompleteDemo() {
    const { say } = useGuide();
    const all = ["White tees", "Hoodies", "DTF gang sheet", "Vinyl banner", "Yard signs", "Business cards", "Window decals", "Tote bags"];
    const [q, setQ] = useState("");
    const hits = q ? all.filter((a) => a.toLowerCase().includes(q.toLowerCase())) : [];
    return (
        <DemoCard title="Search with suggestions" hint="type 'si'" testid="demo-autocomplete">
            <div className="relative">
                <Search size={14} className="absolute left-3 top-3.5 text-zinc-500" />
                <input value={q} onChange={(e) => { setQ(e.target.value); if (e.target.value.length === 2) say("Instant search across your whole catalog. Customers find it in two keystrokes.", "autocomplete"); }}
                    className={`${field} pl-9`} placeholder="Search products…" data-testid="demo-autocomplete-input" />
                {hits.length > 0 && (
                    <div className="absolute z-10 mt-1.5 w-full overflow-hidden rounded-xl border border-white/12 bg-black/95 backdrop-blur">
                        {hits.map((h) => <p key={h} className="cursor-pointer px-3.5 py-2 text-sm hover:bg-brand-green/15 hover:text-brand-green">{h}</p>)}
                    </div>
                )}
            </div>
        </DemoCard>
    );
}

export function ToggleDemo() {
    const { say } = useGuide();
    const [on, setOn] = useState(false);
    return (
        <DemoCard title="Toggle switch" hint="flip it" testid="demo-toggle">
            <button onClick={() => { setOn(!on); say(!on ? "Toggles for rush jobs, add-ons, monthly vs yearly pricing. One tap changes the price." : "Off again. Every toggle can change what the customer pays in real time.", "toggle"); }}
                className={`flex h-9 w-16 items-center rounded-full p-1 transition-colors ${on ? "bg-brand-green" : "bg-white/15"}`} data-testid="demo-toggle-btn">
                <motion.span layout className="h-7 w-7 rounded-full bg-white" style={{ marginLeft: on ? "1.75rem" : 0 }} />
            </button>
            <p className="mt-2.5 text-sm">{on ? <span className="text-brand-green">Rush: +$25 · ready today</span> : <span className="text-zinc-400">Standard turnaround</span>}</p>
        </DemoCard>
    );
}

export function AccordionDemo() {
    const { say } = useGuide();
    const rows = [["Do I own the site?", "Yes. Code, domain, hosting keys — all yours."], ["Really 7 days?", "Really. Day 1 we talk, day 7 you're live."], ["Can I edit it?", "Yes — a real admin panel, not a developer ticket."]];
    const [open, setOpen] = useState(0);
    return (
        <DemoCard title="FAQ accordion" hint="open one" testid="demo-accordion">
            <div className="divide-y divide-white/10 overflow-hidden rounded-xl border border-white/10">
                {rows.map(([q, a], i) => (
                    <button key={q} onClick={() => { setOpen(open === i ? -1 : i); say("Accordions answer objections without cluttering the page. Fewer phone calls for you.", "accordion"); }}
                        className="block w-full px-3.5 py-3 text-left" data-testid={`demo-accordion-${i}`}>
                        <span className="flex items-center justify-between text-sm font-bold">{q} <ChevronDown size={14} className={`transition-transform ${open === i ? "rotate-180 text-brand-green" : "text-zinc-500"}`} /></span>
                        {open === i && <p className="mt-1.5 text-xs text-zinc-400">{a}</p>}
                    </button>
                ))}
            </div>
        </DemoCard>
    );
}

export function DatePickerDemo() {
    const { say } = useGuide();
    const [d, setD] = useState(null);
    const days = ["Mon 8", "Tue 9", "Wed 10", "Thu 11", "Fri 12", "Sat 13"];
    return (
        <DemoCard title="Pick a date" hint="choose a day" testid="demo-datepicker">
            <div className="grid grid-cols-3 gap-2">
                {days.map((x, i) => (
                    <button key={x} onClick={() => { setD(x); say("Date pickers that know your real schedule — closed Sundays, booked Fridays, rush slots priced higher.", "datepicker"); }}
                        className={`rounded-xl border px-2 py-2.5 text-xs font-bold transition-colors ${d === x ? "border-brand-green bg-brand-green text-black" : i === 4 ? "border-white/10 text-zinc-600 line-through" : "border-white/12 text-zinc-300 hover:border-brand-green/50"}`}
                        disabled={i === 4} data-testid={`demo-date-${i}`}>
                        {x}
                    </button>
                ))}
            </div>
            <p className="mt-2.5 flex items-center gap-1.5 text-xs text-zinc-500"><Calendar size={12} /> {d ? `${d} — pickup slot held for 10 min` : "Fri 12 is full — that's real availability"}</p>
        </DemoCard>
    );
}

export function StepperDemo() {
    const { say } = useGuide();
    const [n, setN] = useState(12);
    return (
        <DemoCard title="Quantity stepper" hint="+ / −" testid="demo-stepper">
            <div className="flex items-center gap-3">
                <button onClick={() => { setN(Math.max(1, n - 6)); say("Steppers beat typing. Six at a time, twelve at a time — your minimums, built in.", "stepper"); }}
                    className="h-10 w-10 rounded-xl border border-white/15 text-lg font-black" data-testid="demo-stepper-minus">−</button>
                <span className="font-display text-2xl font-black">{n}</span>
                <button onClick={() => { setN(n + 6); say("Every increment can re-price the order instantly. No 'we'll get back to you'.", "stepper"); }}
                    className="h-10 w-10 rounded-xl border border-white/15 text-lg font-black" data-testid="demo-stepper-plus">+</button>
                <span className="ml-auto font-mono text-sm text-brand-green">${(n * 14).toFixed(0)}</span>
            </div>
        </DemoCard>
    );
}
